import { FC, useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { GuideSection } from '../core'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { typeScale } from '@/shared/styles/typography'

type GuideTocProps = {
  sections: GuideSection[]
}

/**
 * 28 spec の sidebar 幅 (LAYOUT.sidebarWidth = 224) は使わない。あれは短い
 * ページ名を並べる前提で、この目次の項目は「音はきれいなのに、内容が欠けている」
 * のような事象の文。224 だと折り返しが増えて切り分けの表として読めない。
 */
export const TOC_WIDTH = 280

type TocEntry = {
  id: string
  title: string
  isStep: boolean
}

/**
 * 左の固定目次。区分（A〜D）と、その下の手順（A-1…）を2階層で並べる。
 *
 * 目次を上から読むこと自体が対処の順番になる、というのがこの画面の骨格。
 * そのため畳まず、全区分・全手順を開いたまま出す。
 * 階層はインデントと文字色だけで示し、罫線・記号を足さない。
 * 現在地は塗り (overlay.selected) だけで示し、太字や文字色を重ねない。
 */
export const GuideToc: FC<GuideTocProps> = ({ sections }) => {
  const entries = useMemo<TocEntry[]>(
    () =>
      sections.flatMap((s) => [
        {
          id: s.id,
          title: s.label ? `${s.label}. ${s.title}` : s.title,
          isStep: false,
        },
        ...s.steps.map((step) => ({
          id: step.id,
          title: step.title,
          isStep: true,
        })),
      ]),
    [sections]
  )

  const [activeId, setActiveId] = useState<string>(entries[0]?.id ?? '')

  useEffect(() => {
    // 観測するのは見出しだけ。見出しは高さが薄いので、帯に入った最上段を
    // 現在地とみなせる。節の箱ごと観測すると常に節が最上段になり、
    // 手順が現在地にならない。
    const observer = new IntersectionObserver(
      (records) => {
        const visible = records
          .filter((r) => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveId(visible[0].target.id)
      },
      { rootMargin: '-80px 0px -70% 0px' }
    )

    entries.forEach((e) => {
      const el = document.getElementById(e.id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [entries])

  // 目次は手順まで開くと縦に伸び、自前のスクロール領域に入る。
  // 現在地が目次の見えない位置にあると、目次を上から読むという骨格が崩れる。
  useEffect(() => {
    document
      .querySelector(`[data-toc-id="${activeId}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeId])

  return (
    <Box
      component="nav"
      aria-label="目次"
      sx={{
        display: { xs: 'none', md: 'block' },
        position: 'sticky',
        top: 40,
        alignSelf: 'flex-start',
        width: TOC_WIDTH,
        flexShrink: 0,
        maxHeight: 'calc(100vh - 80px)',
        overflowY: 'auto',
      }}
    >
      <Typography
        sx={{
          ...typeScale.tag,
          color: colorTokens.fg.muted,
          mb: 1,
          px: 1.5,
        }}
      >
        目次
      </Typography>
      {entries.map((e) => (
        <Box
          key={e.id}
          component="a"
          href={`#${e.id}`}
          data-toc-id={e.id}
          sx={{
            display: 'block',
            pl: e.isStep ? 3 : 1.5,
            pr: 1.5,
            py: 0.5,
            mt: e.isStep ? 0 : 1,
            borderRadius: `${RADIUS.lg}px`,
            textDecoration: 'none',
            ...typeScale.bodySm,
            color: e.isStep ? colorTokens.fg.muted : colorTokens.fg.default,
            backgroundColor:
              e.id === activeId ? colorTokens.overlay.selected : 'transparent',
            '&:hover': { backgroundColor: colorTokens.overlay.hover },
          }}
        >
          {e.title}
        </Box>
      ))}
    </Box>
  )
}
